import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SupportContact {
  whatsapp: string; // digits only, e.g. "5569920019314"
  phone: string;    // digits only with country code
  email: string;
  cnpj: string;
  address: string;
  instagram: string; // full URL or handle
  facebook: string;
  linkedin: string;
  youtube: string;
  x: string; // Twitter/X
}

const DEFAULT_CONTACT: SupportContact = {
  whatsapp: '',
  phone: '',
  email: '',
  cnpj: '',
  address: '',
  instagram: '',
  facebook: '',
  linkedin: '',
  youtube: '',
  x: '',
};

const SETTING_KEY = 'support_contact';

export function useSupportContact() {
  const [contact, setContact] = useState<SupportContact>(DEFAULT_CONTACT);
  const [isLoading, setIsLoading] = useState(true);

  const fetchContact = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', SETTING_KEY)
        .maybeSingle();

      if (error) {
        console.error('Error fetching support contact:', error);
        return;
      }

      if (data?.value) {
        const value = data.value as unknown as Partial<SupportContact>;
        setContact({ ...DEFAULT_CONTACT, ...value });
      }
    } catch (error) {
      console.error('Error fetching support contact:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateContact = async (next: Partial<SupportContact>) => {
    try {
      const merged: SupportContact = { ...contact, ...next };
      const cleaned: SupportContact = {
        whatsapp: merged.whatsapp.replace(/\D/g, ''),
        phone: merged.phone.replace(/\D/g, ''),
        email: merged.email.trim(),
        cnpj: merged.cnpj.trim(),
        address: merged.address.trim(),
        instagram: normalizeSocialUrl(merged.instagram, 'instagram.com'),
        facebook: normalizeSocialUrl(merged.facebook, 'facebook.com'),
        linkedin: normalizeSocialUrl(merged.linkedin, 'linkedin.com/in'),
        youtube: normalizeSocialUrl(merged.youtube, 'youtube.com/@'),
        x: normalizeSocialUrl(merged.x, 'x.com'),
      };

      const { data: existing } = await supabase
        .from('system_settings')
        .select('id')
        .eq('key', SETTING_KEY)
        .maybeSingle();

      let error;
      if (existing) {
        const { error: updateError } = await supabase
          .from('system_settings')
          .update({ value: JSON.parse(JSON.stringify(cleaned)), updated_at: new Date().toISOString() })
          .eq('key', SETTING_KEY);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('system_settings')
          .insert({ key: SETTING_KEY, value: JSON.parse(JSON.stringify(cleaned)) });
        error = insertError;
      }

      if (error) throw error;
      setContact(cleaned);
      return true;
    } catch (error) {
      console.error('Error updating support contact:', error);
      return false;
    }
  };

  useEffect(() => {
    fetchContact();
  }, []);

  return { contact, isLoading, updateContact, refetch: fetchContact };
}

export function buildWhatsAppUrl(phoneDigits: string, message = '') {
  const phone = (phoneDigits || '').replace(/\D/g, '');
  return `https://wa.me/${phone}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
}

export function formatBrPhone(digits: string): string {
  const d = (digits || '').replace(/\D/g, '');
  const local = d.startsWith('55') ? d.slice(2) : d;
  if (local.length === 11) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  }
  if (local.length === 10) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  }
  return digits;
}

/**
 * Accepts full URL or a handle and returns a normalized full URL.
 * Empty input stays empty.
 */
export function normalizeSocialUrl(input: string, domainPath: string): string {
  const v = (input || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@+/, '').replace(/^\/+/, '');
  return `https://${domainPath}/${handle}`;
}
